import { Module, GetterTree, MutationTree, ActionTree } from 'vuex'
import { forOwn } from 'lodash'

import { vm } from '../../main'
import RootState from '../RootState'
import UserState from '../UserState'

import { mapData, USER_API_MAPPING, UserType } from '../../api/Mapping'
import User from '../../models/User'
import Right from '../../models/Right'
import { MutationType as RootMutationType } from '../index'

import { Error } from '../../api/Error'
import { checkError } from '../Error'

const INITIAL_STATE: UserState = {
  id: null,
  firstName: null,
  lastName: null,
  email: null,
  phone: null,
  phoneVerified: false,
  company: null,
  locale: null,
  updatedAt: null,
  createdAt: null,
  avatarID: null,
  token: null,
  fullName: null,
  entrepriseId: null,
  companyLogoId: null,
  master: false,
  trial: false,
  trialRemaining: null,
  trialExpired: false,
  regular: false,
  premium: false,
  accesses: {
    trial: false,
    reports: false,
    sms: false,
    api: false,
    customization: false,
    team: false,
    premium: false,
    support: false,
  }
}

const userState: UserState = INITIAL_STATE

const userGetters: GetterTree<UserState, RootState> = {
  locale(state): string {
    return state.locale
  },
  logged(state): boolean {
    return !!state.token
  },
  profile(state) {
    return state
  },
  fullName(state) {
    return [state.firstName, state.lastName].filter((x) => x != null && x.length > 0).join(' ')
  },
  master(state) {
    return state.master
  },
  premium(state) {
    return state.premium
  },
  infos(state) {
    return {
      fullName: state.fullName,
      lastName: state.lastName,
      company: state.company,
      firstName: state.firstName,
      email: state.email,
      phone: state.phone
    }
  },
  canTrial(state) {
    return state.accesses.trial
  },
  canSendSMS(state) {
    return state.accesses.sms
  },
  isTrial(state) {
    return state.trial
  },
  trialRemaining(state) {
    return state.trialRemaining
  },
  trialExpired(state) {
    return state.trialExpired
  },
  companyLogoId(state) {
    return state.companyLogoId > 0 ? state.companyLogoId : null
  },
  userId(state) {
    return state.id
  },
  isAdmin(state) {
    return state.entrepriseId === null || state.entrepriseId === 0 || state.entrepriseId === state.id
  }
}

enum MutationType {
  SetLocale = 'setLocale',
  SetUser = 'setUser',
  ResetState = 'resetState',
  SetRights = 'setRights',
  SetCompanyLogoId = 'setCompanyLogoId'
}

const userMutations: MutationTree<UserState> = {
  [MutationType.SetLocale]: (state, payload) => {
    state.locale = payload
  },
  [MutationType.SetUser]: (state, payload) => {
    forOwn(payload, (value, key) => {
      state[key] = value
    })
    state.master = !!!payload.entrepriseId
    if (payload.firstName && payload.lastName) {
      state.fullName = `${payload.firstName} ${payload.lastName}`
    }
  },
  [MutationType.ResetState]: (state, payload) => {
    forOwn(INITIAL_STATE, (value, key) => {
      state[key] = value
    })
  },
  [MutationType.SetRights]: (state, payload) => {
    forOwn(payload, (value, key) => {
      state[key] = typeof(value) === 'object' ? {...value} : value
    })
  },
  [MutationType.SetCompanyLogoId]: (state, payload) => {
    state.companyLogoId = payload
  }
}

const userActions: ActionTree<UserState, RootState> = {
  setUserLocale({ commit }, payload) {
    commit(MutationType.SetLocale, payload)
  },
  async updateUserLocal({ commit, rootGetters }, payload) {
    try {
      const res = await vm.$api.user.updateLang(rootGetters.token, payload)
      commit(MutationType.SetLocale, payload)
    } catch (error) {
      checkError(commit, error)
      throw error
    }
  },
  async createUser({ commit, dispatch }, payload) {
    try {
      const res = await vm.$api.user.signUp(payload.email, payload.password)
      const newUser = new User(mapData(USER_API_MAPPING, res.data, false, true))
      commit(MutationType.SetUser, newUser)
      commit(RootMutationType.SetToken, newUser.getToken, { root: true })
      await dispatch('updateUserRights')
    } catch (error) {
      checkError(commit, error)
      throw error
    }
  },
  async createRegistration({ commit, dispatch }, payload) {
    try {
      const res = await vm.$api.user.login(payload.identifier, payload.password)
      const newUser = new User(mapData(USER_API_MAPPING, res.data, false, true))
      commit(MutationType.SetUser, newUser)
      commit(RootMutationType.SetToken, newUser.getToken, { root: true })
      await dispatch('updateUserRights')
    } catch (error) {
      checkError(commit, error)
      throw error
    }
  },
  destroyRegistration({ commit }) {
    commit(MutationType.ResetState)
    commit(RootMutationType.ResetState, {}, { root: true })
  },
  async updatePhoneNumber({ commit, rootGetters }, payload) {
    try {
      const phoneNumber = payload.replace(/ /g, '')
      const res = await vm.$api.user.updatePhoneNumber(rootGetters.token, phoneNumber)
      const newUser = new User(mapData(USER_API_MAPPING, res.data, false, true))
      commit(MutationType.SetUser, newUser)
    } catch (error) {
      checkError(commit, error)
      throw error
    }
  },
  async checkPhoneNumber({ commit, rootGetters }, payload) {
    try {
      const res = await vm.$api.user.checkPhoneNumber(rootGetters.token, payload)
      const newUser = new User(mapData(USER_API_MAPPING, res.data, false, true))
      commit(MutationType.SetUser, newUser)
    } catch (error) {
      checkError(commit, error)
      throw error
    }
  },
  async updateInformations({ commit, rootGetters }, payload) {
    try {
      const res = await vm.$api.user.updateInformations(rootGetters.token, payload)
      const newUser = new User(mapData(USER_API_MAPPING, res.data, false, true))
      commit(MutationType.SetUser, newUser)
    } catch (error) {
      checkError(commit, error)
      throw error
    }
  },
  async updateAvatar({ commit, rootGetters }, payload) {
    try {
      const res = await vm.$api.user.updateAvatar(rootGetters.token, payload)
      const newUser = new User(mapData(USER_API_MAPPING, res.data, false, true))
      commit(MutationType.SetUser, newUser)
    } catch (error) {
      checkError(commit, error)
      throw error
    }
  },
  async updateUserRights({ commit, rootGetters, dispatch }) {
    try {
      const res = await vm.$api.user.getRights(rootGetters.token)
      const abilities = new Right(res.data)

      commit(MutationType.SetRights, abilities)
      dispatch('updateAccess', abilities.premium || abilities.trial, { root: true })
    } catch (error) {
      checkError(commit, error)
      throw error
    }
  },
  async updateCompanyLogoId({ commit, rootGetters }, payload) {
    try {
      const res = await vm.$api.company.updateLogo(rootGetters.token, payload)
      const newUser = new User(mapData(USER_API_MAPPING, res.data, false, true))
      commit(MutationType.SetUser, newUser)
    } catch (error) {
      checkError(commit, error)
      throw error
    }
  }
}

export const user: Module<UserState, RootState> = {
  namespaced: true,
  state: userState,
  getters: userGetters,
  mutations: userMutations,
  actions: userActions
}
